import { useEffect, useRef } from 'react';

// REDUX
import { IBetState, IErrorState } from '@GameStore/GameStoreSchema';
import EventsCenter from '@Managers/Events/EventsCenter';
import { EGameEvents } from '@Managers/Events/GameEvents';
import { getAccount, getPopClient, getReadyState } from '@Managers/Redux/AppState/AppState';
import { useAppSelector } from '@Managers/Redux/Hooks';
import { EGameStates } from '@Managers/StateManager/GameStates';
import { TAssetsLoaderState } from '@UILayer/Views/Preloader/Preloader.types';
import { EPopClientMessageType, IOpenPageData, IPopClientMessage } from './UsePopClient.types';

export const UsePopClientGame2World = (): {
    g2wMessage: typeof g2wMessage;
} => {
    const config = useAppSelector(getPopClient);
    const readyState = useAppSelector(getReadyState);
    const gameStoreAccount = useAppSelector(getAccount);

    const configRef = useRef(config);
    configRef.current = config;

    useEffect(() => {
        if (Object.keys(config.supportedFeatures).length > 0) {
            g2wInit();
        }
    }, [config]);

    useEffect(() => {
        if (readyState) {
            g2wMessage({ _type: EPopClientMessageType.AUTOPLAY_G2W_AUTOPLAY_START_NOTIFICATION });
        }
    }, [readyState]);

    useEffect(() => {
        balanceUpdateHalnder();
    }, [gameStoreAccount]);

    function g2wInit() {
        EventsCenter.on(EGameEvents.ASSET_LOADER_INFORMATION, loaderHandler);
        EventsCenter.on(EGameEvents.AUTOPLAY_MODE_START, () => autoplayHandler(true));
        EventsCenter.on(EGameEvents.AUTOPLAY_MODE_END, () => autoplayHandler(false));
        EventsCenter.on(EGameStates.GAME_START, gameReadyHandler);
        EventsCenter.on(EGameEvents.TRANSACTION_CYCLE_START, gameRoundStartHandler);
        EventsCenter.on(EGameEvents.TRANSACTION_CYCLE_END, gameRoundEndHandler);
        EventsCenter.on(EGameEvents.BET_UPDATE, betUpdateHandler);
        EventsCenter.on(EGameEvents.MUTE, () => soundHandler(true));
        EventsCenter.on(EGameEvents.UNMUTE, () => soundHandler(false));

        EventsCenter.on(EGameEvents.OPEN_PAGE, (data: IOpenPageData) => openPageHandler(data));
        EventsCenter.on(EGameEvents.PAUSE_STATUS, ({ data }: { data: boolean }) => pauseStatusHandler(data));
        EventsCenter.on(EGameEvents.UI_STATE, (data: { busy: boolean }) => uiStateHandler(data));
        EventsCenter.on(EGameEvents.ERROR, errorHandler);
        EventsCenter.on(EGameEvents.CLOSE_GAME_FRAME_COMMAND, closeGameFrameHandler);
    }

    const balanceUpdateHalnder = () => {
        const account = gameStoreAccount;
        g2wMessage({
            _type: EPopClientMessageType.BALANCEUPDATE_G2W_BALANCE_UPDATE_NOTIFICATION,
            balance: {
                value: account.balance.normalized,
                winAmount: account.win.normalized
            }
        });
    };

    const closeGameFrameHandler = () => {
        g2wMessage(
            {
                _type: EPopClientMessageType.CLOSEGAME_G2W_BASIC_GAME_FRAME_COMMAND
            },
            false
        );
    };

    const errorHandler = (data: IErrorState) => {
        g2wMessage({
            _type: EPopClientMessageType.ERRORS_G2W_ERROR_NOTIFICATION,
            error: {
                type: data.category,
                description: data.error
            }
        });
    };

    const uiStateHandler = (data: { busy: boolean }) => {
        // TO DO
        g2wMessage({
            _type: EPopClientMessageType.DISPLAYSTATUS_G2W_UI_STATE_NOTIFICATION,
            busy: data.busy
        });
    };

    const pauseStatusHandler = (data: boolean) => {
        g2wMessage({
            _type: EPopClientMessageType.PAUSE_G2W_PAUSE_NOTIFICATION,
            pause: data
        });
    };

    const openPageHandler = (data: IOpenPageData) => {
        g2wMessage({
            _type: EPopClientMessageType.OPENPAGE_G2W_OPEN_PAGE_COMMAND,
            pageID: data.pageID,
            url: data.url
        });
    };

    const betUpdateHandler = (data: IBetState) => {
        g2wMessage({
            _type: EPopClientMessageType.BETUPDATE_G2W_GAME_UPDATE_NOTIFICATION,
            totalBet: data.value.formatted
        });
    };

    const gameRoundStartHandler = () => {
        g2wMessage({ _type: EPopClientMessageType.GAMEROUND_G2W_GAME_ROUND_START_NOTIFICATION });
    };

    const gameRoundEndHandler = () => {
        g2wMessage({ _type: EPopClientMessageType.GAMEROUND_G2W_GAME_ROUND_END_NOTIFICATION });
    };

    const gameReadyHandler = () => {
        g2wMessage({ _type: EPopClientMessageType.LOADINGSTATUS_G2W_GAME_READY_NOTIFICATION });
    };

    const soundHandler = (mute: boolean) => {
        const soundPayload = {
            mute: mute,
            level: mute ? 0 : 100
        };
        g2wMessage({ _type: EPopClientMessageType.SOUND_G2W_SOUND_UPDATE_NOTIFICATION, ...soundPayload });
    };

    const autoplayHandler = (start: boolean) => {
        if (start) {
            g2wMessage({ _type: EPopClientMessageType.AUTOPLAY_G2W_AUTOPLAY_START_NOTIFICATION });
        } else {
            g2wMessage({ _type: EPopClientMessageType.AUTOPLAY_G2W_AUTOPLAY_END_NOTIFICATION });
        }
    };

    const loaderHandler = (data: Partial<TAssetsLoaderState>) => {
        if (data.loaderState && data.loaderState === 'START') {
            g2wMessage({ _type: EPopClientMessageType.LOADINGSTATUS_G2W_START_NOTIFICATION });
        }
        if (data.loaderState && data.loaderState === 'COMPLETE') {
            g2wMessage({ _type: EPopClientMessageType.LOADINGSTATUS_G2W_END_NOTIFICATION });
        }
        if (data.progress) {
            g2wMessage({ _type: EPopClientMessageType.LOADINGSTATUS_G2W_PROGRESS_NOTIFICATION, progress: Number((data.progress * 100).toFixed()) });
        }
    };

    const g2wMessage = (data: IPopClientMessage, typeCheck: boolean = true) => {
        let requestString = JSON.stringify(data);

        if (typeCheck) {
            let type = data._type.split('.')[1];
            if (configRef.current.supportedFeatures[type]) {
                window.parent.postMessage(requestString, '*');
            }
        } else {
            window.parent.postMessage(requestString, '*');
        }
    };

    return {
        g2wMessage
    };
};
